import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import IconButton from 'components/common/IconButton';
import { errorNotification, handleApiError, successNotification } from 'utils';
import { uploadEpstQuestions } from 'api/files/files';

/**
 * @component UploadEPST
 * @description
 * Componente para importar y subir archivos de preguntas EPST en formato Excel (.xls o .xlsx).
 * Permite seleccionar, validar, visualizar y enviar el archivo a la API, además de resetearlo.
 *
 * @param {Function} onSubmitFile - Función callback que se ejecuta cuando el archivo se sube correctamente.
 */
function UploadEPST({ onSubmitFile }) {
	const token = useSelector(state => state.authentication.user?.token);

	const [file, setFile] = useState(null);
	const fileInputRef = useRef(null);

	/**
	 * @function fetchUploadFiles
	 * @description Envia el archivo a la API para cargar preguntas EPST.
	 * @param {FormData} form - FormData con el archivo a subir.
	 * @param {string} newFileName - Nombre original del archivo.
	 */
	const fetchUploadFiles = async (form, newFileName) => {
		try {
			const response = await uploadEpstQuestions(form, token);
			if (response.data.status === 200) {
				successNotification("Archivo cargado correctamente.");
				onSubmitFile(response.data);
				handleFileReset();
			} else {
				errorNotification("No es posible subir el archivo.");
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * @function handleFileChange
	 * @description Valida la extensión del archivo seleccionado y actualiza el estado.
	 * @param {Event} event - Evento de cambio de input file.
	 */
	const handleFileChange = (event) => {
		const selectedFile = event.target.files[0];

		if (selectedFile) {
			const fileName = selectedFile.name.toLowerCase();
			if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
				setFile(selectedFile);
			} else {
				errorNotification('Formato de archivo no válido. Por favor, selecciona un archivo en formato xls.');
				setFile(null);
			}
		}
	};

	/**
	 * @function handleFileInputClick
	 * @description Lanza el input file oculto al hacer clic en el botón.
	 */
	const handleFileInputClick = () => {
		fileInputRef.current.click();
	};

	/**
	 * @function handleUpload
	 * @description Prepara el FormData y envía el archivo si existe.
	 */
	const handleUpload = async () => {
		if (file) {
			const form = new FormData();
			const newFileName = `${file.name}`;
			form.append('uploaded_file', file, newFileName);
			fetchUploadFiles(form, newFileName);
		} else {
			errorNotification("Es necesario seleccionar un archivo.");
		}
	};

	/**
	 * @function handleFileReset
	 * @description Limpia el archivo seleccionado y el input.
	 */
	const handleFileReset = () => {
		setFile(null);
		fileInputRef.current.value = '';
	};

	return (
		<>
			<input
				type="file"
				className="form-control"
				id="submitFile"
				onChange={handleFileChange}
				style={{ display: 'none' }}
				ref={fileInputRef}
			/>
			<IconButton
				variant="falcon-default"
				size="sm"
				icon={file ? "file-upload" : "file-import"}
				transform="shrink-3"
				className="me-2"
				iconAlign="middle"
				onClick={file ? handleUpload : handleFileInputClick}
			>
				<span className="d-none d-sm-inline-block d-xl-none d-xxl-inline-block ms-1">
					{file ? 'Subir Archivo' : 'Importar'}
				</span>
			</IconButton>
			{file ? (
				<>
					<span className="text-success me-2">{file.name}</span>
					<IconButton
						variant="falcon-default"
						size="sm"
						icon={"times"}
						transform="shrink-3"
						className="me-2"
						iconAlign="middle"
						onClick={handleFileReset}
					/>
				</>
			) : (
				<span className="text-muted"></span>
			)}
		</>
	);
}

export default UploadEPST;